import * as Sentry from "@sentry/nextjs"
import { Alert, Button, Spinner, TextInput } from "flowbite-react"
import { Form, FormikProvider, useFormik } from "formik"
import Link from "next/link"
import { useRouter } from "next/router"
import React, { useEffect, useMemo, useState } from "react"
import * as Yup from "yup"

import { ArrowLeftIcon } from "@assets/icons"
import FormGroup from "@components/elements/FormGroup"
import PasswordInput from "@components/elements/PasswordInput"
import useAuth from "@hooks/useAuth"
import useToast from "@hooks/useToast"
import Google from "@icons/Google"
import routes from "@mono/essentials/constants/routes"
import * as CustomerServices from "@mono/essentials/services/CustomerService"
import { SendOtpFieldType, SendOtpResponseType } from "@mono/essentials/types/CustomerTypes"
import { getDomainName } from "@mono/essentials/utils/Common"

import CustomerOtpVerificationModal from "./CustomerOtpVerificationModal"

import type { FormikHelpers } from "formik"

export type CustomerLoginFormAttributes = {
  email: string
  password: string
  signinWith: "otp" | "password"
}

interface CustomerLoginFormProps {
  className?: string
  isRedirectEnabled?: boolean
  onSuccess?: () => Promise<void> | void
  onSuccessCustomerLogin?: () => void
  isIntegratedPage?: boolean
  disableGoogleSignin?: boolean
}

const CustomerLoginForm = ({
  className,
  isRedirectEnabled = true,
  onSuccessCustomerLogin,
  isIntegratedPage = false,
  onSuccess,
  disableGoogleSignin = false,
}: CustomerLoginFormProps) => {
  const router = useRouter()
  const queryParams = router.query
  const { showToast } = useToast()
  const {
    setToken,
    setSelectedDomain,
    setSelectedDomainName,
    setSelectedDomainTimeZone,
    setSelectedDomainCurrency,
    setIsCustomerLoggedIn,
    setLoggedInCustomerDetails,
    publicDomainDetails,
    setSelectedTenant,
  } = useAuth() ?? {}

  const [formError, setFormError] = useState<string>()
  const [isGoogleSigninInprogress, setIsGoogleSigninInprogress] = useState<boolean>(false)
  const [showOtpVerificationModal, setShowOtpVerificationModal] =
    useState<SendOtpResponseType>(undefined)

  const initialValues: CustomerLoginFormAttributes = useMemo(
    () => ({
      email: "",
      password: "",
      signinWith: "otp",
    }),
    []
  )

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(`Please enter your account's email`)
      .required("*Please enter your email"),
    password: Yup.string().when("signinWith", (signinWith, field) =>
      signinWith[0] === "password"
        ? field.required("*Password Required").min(8, "Too Short!")
        : field
    ),
    signinWith: Yup.string().required(),
  })

  const onSubmit = async (
    values: CustomerLoginFormAttributes,
    { setSubmitting, setFieldError }: FormikHelpers<CustomerLoginFormAttributes>
  ) => {
    try {
      setFormError("")
      setSubmitting(true)
      const { data: loginResponse } = await CustomerServices.signinCustomerWithPassword({
        email: values.email,
        password: values.password,
      })
      if (onSuccess) {
        await onSuccess()
      } else {
        if (loginResponse?.token.access_token) {
          setToken?.(loginResponse.token.access_token)
          setIsCustomerLoggedIn?.(true)
          setLoggedInCustomerDetails?.(loginResponse.customer)
          setSelectedTenant?.(loginResponse.tenant.id)
          if (isRedirectEnabled) {
            router.push(
              `${window.location.protocol}//${
                publicDomainDetails.domain
              }.${getDomainName(true)}${routes.customer.orders.root}`
            )
          }
          onSuccessCustomerLogin?.()
        } else {
          setFormError("Something went wrong! please try again.")
        }
      }
    } catch (e: any) {
      Sentry.captureException(e)
      const errors = e?.response?.data?.errors || {}
      for (const key of Object.keys(errors)) {
        setFieldError(key, errors[key][0])
      }
      if (e?.response?.data?.message) {
        setFormError(e?.response?.data?.message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const loginForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  })
  const {
    getFieldProps,
    errors,
    values,
    touched,
    isSubmitting,
    isValid,
    setFieldValue,
    setSubmitting,
  } = loginForm

  const initiateGoogleSignin = async () => {
    try {
      const { data } = await CustomerServices.customerGoogleSignin()
      window.location.href = data.url
    } catch (e) {
      Sentry.captureException(e)
      showToast({
        heading:
          e?.response?.data?.message ?? e?.message ?? "Unable to login! please try again later.",
        type: "danger",
      })
    }
  }

  const handleGoogleSigninCallback = async () => {
    try {
      setFormError("")
      setIsGoogleSigninInprogress(true)
      const { data: loginResponse } = await CustomerServices.customerGoogleSigninCallback(
        location.search
      )
      if (onSuccess) {
        await onSuccess()
      } else {
        if (loginResponse?.token.access_token) {
          setToken?.(loginResponse.token.access_token)
          setIsCustomerLoggedIn?.(true)
          setSelectedTenant?.(loginResponse.tenant.id)
          setLoggedInCustomerDetails?.(loginResponse.customer)
          if (isRedirectEnabled) {
            router.push(
              `${window.location.protocol}//${
                loginResponse.tenant.domain.domain
              }.${getDomainName(true)}${routes.customer.orders.root}`
            )
          }
          onSuccessCustomerLogin?.()
        } else {
          setFormError("Something went wrong! please try again.")
        }
      }
    } catch (e: any) {
      Sentry.captureException(e)
      if (e?.response?.data?.message) {
        setFormError(e?.response?.data?.message)
      }
    } finally {
      setIsGoogleSigninInprogress(false)
    }
  }

  useEffect(() => {
    if (publicDomainDetails) {
      setSelectedDomain?.(publicDomainDetails.id)
      setSelectedDomainTimeZone?.(publicDomainDetails.timezone_iso3)
      setSelectedDomainCurrency?.(publicDomainDetails.currency_symbol)
      setSelectedDomainName?.(publicDomainDetails.domain)
    }
    if (queryParams?.access_token && queryParams?.tenant_id) {
      setSubmitting(true)
      setToken?.(queryParams.access_token as string)
      setIsCustomerLoggedIn?.(true)
      setSelectedTenant?.(queryParams.tenant_id as string)
      if (isRedirectEnabled) {
        router.push(
          `${window.location.protocol}//${
            publicDomainDetails.domain
          }.${getDomainName(true)}${routes.customer.orders.root}`
        )
      }
      setSubmitting(false)
    }
  }, [publicDomainDetails, queryParams])

  useEffect(() => {
    if (queryParams?.state && queryParams?.code) {
      handleGoogleSigninCallback()
    }
  }, [queryParams])

  const onSuccessOtpVerification = () => {
    setShowOtpVerificationModal(undefined)
    onSuccessCustomerLogin?.()
  }

  const handleSendOtp = async () => {
    try {
      const { data: sendOtpResponse } = await CustomerServices.sendOtp(
        {
          field_type: SendOtpFieldType.EMAIL,
          value: values.email,
          type: "Customer",
        },
        isIntegratedPage
      )
      showToast({
        heading: sendOtpResponse.message,
      })
      if (sendOtpResponse) {
        setShowOtpVerificationModal(sendOtpResponse)
      }
    } catch (e) {
      Sentry.captureException(e)
      showToast({
        heading:
          e?.response?.data?.message ??
          e?.message ??
          "Unable to send the one-time password! please try again later.",
        type: "danger",
      })
    }
  }

  return (
    <div className={className}>
      {formError && (
        <Alert color={"failure"} className={"mb-3"}>
          <span>{formError}</span>
        </Alert>
      )}
      <FormikProvider value={loginForm}>
        <Form autoComplete={"off"} className={"pb-6"} id={"customer-login-form"}>
          <div className={"flex flex-col items-center w-full gap-5"}>
            {!disableGoogleSignin ? (
              <>
                <Button
                  color={"ghost"}
                  size={"lg"}
                  onClick={initiateGoogleSignin}
                  className={"w-full border border-neutral-300 overflow-hidden rounded-lg"}
                  id={"signinButton"}
                  disabled={isGoogleSigninInprogress}
                >
                  <Google width={24} height={24} />
                  <span className={"text16Medium text-neutral-1000 mx-2"}>Sign in With Google</span>
                  {isGoogleSigninInprogress ? <Spinner size={"sm"} light /> : null}
                </Button>
                <div className={"flex flex-row items-center gap-2 w-full"}>
                  <div className={"flex-1 h-px bg-neutral-300"} />
                  <p className={"text12Medium text-neutral-150"}>Or Sign in With</p>
                  <div className={"flex-1 h-px bg-neutral-300"} />
                </div>
              </>
            ) : null}
            <div className={"flex flex-col gap-5 w-full"}>
              <FormGroup label={"Email"} name={"email"}>
                <TextInput
                  {...getFieldProps("email")}
                  sizing={"lg"}
                  type={"email"}
                  className={"solidInput"}
                  placeholder={"e.g. john@example.com"}
                  color={errors.email && touched.email ? "failure" : undefined}
                />
              </FormGroup>
              {values.signinWith === "password" ? (
                <>
                  <div>
                    <FormGroup label={"Password"} name={"password"}>
                      <PasswordInput
                        {...getFieldProps("password")}
                        sizing={"lg"}
                        className={"solidInput"}
                        placeholder={"Password"}
                        color={errors.password && touched.password ? "failure" : undefined}
                      />
                    </FormGroup>
                    <Link href={routes.customer.forgotPassword}>
                      <p
                        className={"pt-2 text-right text12Medium text-primary-600 hover:underline"}
                      >
                        Forgot Password?
                      </p>
                    </Link>
                  </div>
                  <div className={`flex-row items-center gap-3.5 flex mt-2`}>
                    <Button
                      color={"primary"}
                      size={"lg"}
                      className={`w-fit`}
                      onClick={() => {
                        setFieldValue("signinWith", "otp")
                      }}
                    >
                      <ArrowLeftIcon width={18} height={18} className={"text-whiteBlack-100"} />
                    </Button>
                    <Button
                      fullSized
                      type={"submit"}
                      size={"lg"}
                      color={"primary"}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <Spinner size={"sm"} light /> : "Sign in"}
                    </Button>
                  </div>
                </>
              ) : null}
              <div
                className={`flex-row items-center gap-3.5 ${
                  values.signinWith === "password" ? "hidden" : "flex"
                }`}
              >
                <Button
                  color={"primary"}
                  size={"lg"}
                  className={`flex-1`}
                  onClick={handleSendOtp}
                  disabled={!isValid || !values.email}
                >
                  <span className={"text16Medium text-whiteBlack-100"}>Sign in With OTP</span>
                </Button>
                <Button
                  color={"ghost"}
                  size={"lg"}
                  className={"flex-1 h-[51.2px] border border-primary-600 overflow-hidden"}
                  onClick={() => {
                    setFieldValue("signinWith", "password")
                  }}
                >
                  <span className={"text16Medium text-primary-600 text-nowrap"}>
                    Sign in With Password
                  </span>
                </Button>
              </div>
              <div className={"-mt-2 text13Medium text-neutral-500 text-center hidden"}>
                Don’t have an account?{" "}
                <span
                  className={"text-primary-600 cursor-pointer"}
                  onClick={() => {
                    console.log("Navigate to sign-up")
                  }}
                >
                  Sign Up
                </span>
              </div>
            </div>
          </div>
        </Form>
      </FormikProvider>
      {showOtpVerificationModal ? (
        <CustomerOtpVerificationModal
          show={Boolean(showOtpVerificationModal)}
          handleClose={() => setShowOtpVerificationModal(undefined)}
          email={values.email}
          isRedirectEnabled={isRedirectEnabled}
          onSuccessCustomerLogin={onSuccessOtpVerification}
          isIntegratedPage={isIntegratedPage}
          initialSendOtpResponse={showOtpVerificationModal}
        />
      ) : null}
    </div>
  )
}

export default CustomerLoginForm
